import React from 'react';
import { message } from 'antd';
import TableColumnTitle from '../../../../../components/_table/TableColumnTitle';
import TableFilterInput from '../../../../../components/_table/TableFilterInput';
import Select from '../../../../../components/input/Select';
import Input from '../../../../../components/input/Input';
import OpenOrderItemDetail from '../../../../../components/link/OpenOrderItemDetail';
import OpenOrderDeliveryTracking from '../../../../../containers/app/common/OpenOrderDeliveryTracking';
import OpenPreviewProductDetail from '../../../../../containers/app/common/OpenPreviewProductDetail';
import OpenProductDetail from '../../../../../components/link/OpenProductDetail';
import OpenOrderModifyDeliveryInfo from '../../../../../containers/app/common/OpenOrderModifyDeliveryInfo';
import OpenBrandDetail from '../../../../../components/link/OpenBrandDetail';
import TableFilterCheckBoxGroup from '../../../../../components/_table/TableFilterCheckBoxGroup';
import { ORDER_MANAGEMENT_LIST_FILTER_OPTION_MODEL } from '../../../../../store/modules/order/models';
import { MAXIMUM_NUMBER_OF_INVOICE_NUMBER, DATE_FORMAT } from '../../../../../constants/service/common/utilConstants';
import {
  ENABLE_SELECT_DELIVERY_TYPE_ORDER_ITEM_STATUS_LIST,
  ORDER_ITEM_DELIVERY_TYPE_SEARCH_SELECT_LIST,
  ORDER_ITEM_STATUS,
  ORDER_DELIVERY_TYPE,
  ORDER_DELIVERY_TYPE_TEXT,
  ENABLE_SELECT_DELIVERY_AGENT_ORDER_ITEM_STATUS_LIST,
  ORDER_ITEM_STATUS_TEXT,
  PAY_METHOD_TYPE_TEXT,
  DELIVERY_INFO_MODIFY_TYPE_SELECT_LIST,
  DELIVERY_INFO_MODIFY_TYPE,
  ORDER_TYPE_TEXT,
  ORDER_GIFT_STATUS_TEXT,
  DELIVERY_INFO_MODIFY_TYPE_TEXT,
} from '../../../../../constants/service/app/orderConstants';
import { PRODUCT_MANAGEMENT_TYPE_TEXT } from '../../../../../constants/service/app/productConstants';
import { BRAND_TYPE_TEXT } from '../../../../../constants/service/app/brandConstants';
import { numberKeyPress, setDash, setNumberWithComma, setPhoneFormat } from '../../../../../lib/common';
import { setDateFormat } from '../../../../../lib/moment';
import { setOrderMessage, setDeliveryInfo, setBuyerAccount } from '../../../../../lib/utils/orderutils';

/**
 *  주문/배송관리 리스트 컬럼
 */
const columns = (data) => {
  const { deliveryAgentList = [], setData, filterOption, onFilter } = data;

  return [
    {
      title: '주문 타입',
      dataIndex: 'orderType',
      render: (value) => setDash(ORDER_TYPE_TEXT[value]),
    },
    {
      title: '선물 상태',
      dataIndex: 'giftStatus',
      render: (value) => setDash(ORDER_GIFT_STATUS_TEXT[value]),
    },
    {
      title: <TableColumnTitle title="주문/배송 정보" color="primary" />,
      children: [
        {
          title: (
            <TableColumnTitle
              title="배송지 변경 이력"
              filterValue={filterOption.modifiedDeliveryInfo
                .map((type) => DELIVERY_INFO_MODIFY_TYPE_TEXT[type])
                .join(', ')}
            />
          ),
          dataIndex: 'orderItemModified',
          filterDropdown: ({ confirm }) => (
            <TableFilterCheckBoxGroup
              list={DELIVERY_INFO_MODIFY_TYPE_SELECT_LIST}
              option={filterOption.modifiedDeliveryInfo}
              initialOption={ORDER_MANAGEMENT_LIST_FILTER_OPTION_MODEL.modifiedDeliveryInfo}
              confirm={confirm}
              onFilter={(value) => {
                if (!value.length) {
                  message.error('배송지 변경 이력 필터 조건은 최소 1개이상 선택하셔야 합니다.');
                  setData({
                    path: ['orderManagementList', 'filterOption', 'modifiedDeliveryInfo'],
                    value: [DELIVERY_INFO_MODIFY_TYPE.YES, DELIVERY_INFO_MODIFY_TYPE.NO],
                  });
                  return;
                }
                onFilter({ modifiedDeliveryInfo: value });
              }}
            />
          ),
          render: (value, record) =>
            record.orderItemModifyId ? <OpenOrderModifyDeliveryInfo orderItemId={record.orderItemId} /> : '-',
        },
        {
          title: '주문번호',
          dataIndex: 'orderId',
        },
        {
          title: '상품 주문번호',
          dataIndex: 'orderItemId',
          render: (value) => <OpenOrderItemDetail text={value} orderItemId={value} />,
        },
        {
          title: '주문 메시지',
          dataIndex: 'orderMessage',
          render: (value) => setOrderMessage(value),
        },
        {
          title: '스타일코드(바코드)',
          dataIndex: 'productStyleCode',
          render: (value) => setDash(value),
        },
        {
          title: '바코드',
          dataIndex: 'productOptionStyleCode',
          render: (value) => setDash(value),
        },
        {
          /**
           *  배송방법
           *  - 상품 주문 상태가 상품 준비(4), 배송 지연(5), 배송 중(6)인 경우에만
           *  배송방법을 선택 또는 변경을 할 수 있음
           *  - 상품 주문 상태가 배송완료(7), 구매확정(8), 취소(9), 반품(10), 교환(11)이고
           *  배송방법이 택배, 등기, 소포인 경우에는 배송방법 텍스트를 보여줌
           *  - 나머지 상품 주문 상태에서는 '-'를 보여줌
           */
          title: <TableColumnTitle className="delivery-type" title="배송방법" />,
          dataIndex: 'deliveryType',
          render: (value, record, index) => {
            /**
             *  상품 준비(4), 배송 지연(5), 배송 중(6)
             */
            if (ENABLE_SELECT_DELIVERY_TYPE_ORDER_ITEM_STATUS_LIST.includes(record.orderItemStatus)) {
              return (
                <Select
                  list={ORDER_ITEM_DELIVERY_TYPE_SEARCH_SELECT_LIST}
                  onChange={(id) =>
                    setData({
                      path: ['orderManagementList', 'list', index],
                      value: {
                        ...record,
                        ...setDeliveryInfo({
                          deliveryType: id,
                          deliveryAgentId: record.deliveryAgentId,
                          invoiceNumber: record.invoiceNumber,
                        }),
                      },
                    })
                  }
                  value={value}
                  size="default"
                  isTable={true}
                />
              );
            } else if (
              /**
               *  배송완료(7), 구매확정(8), 취소(9), 반품(10), 교환(11) && 택배, 등기, 소포
               */
              record.orderItemStatus >= ORDER_ITEM_STATUS.DELIVERY_COMPLETE &&
              (value || value === ORDER_DELIVERY_TYPE.PARCEL_DELIVERY)
            ) {
              return ORDER_DELIVERY_TYPE_TEXT[value];
            } else return '-';
          },
        },
        {
          /**
           *  택배사
           *  - 상품 주문 상태가 상품 준비(4), 배송 지연(5), 배송 중(6)인 경우에만
           *  택배사를 선택 또는 변경을 할 수 있음
           *  - 상품 주문 상태가 배송완료(7), 구매확정(8), 취소(9), 반품(10), 교환(11)인 경우에만
           *  택배사 텍스트를 보여줌
           *  - 나머지 상품 주문 상태에서는 '-'를 보여줌
           */
          title: <TableColumnTitle className="delivery-agent" title="택배사" />,
          dataIndex: 'deliveryAgentId',
          render: (value, record, index) => {
            /**
             *  상품 준비(4), 배송 지연(5), 배송 중(6)
             */
            if (ENABLE_SELECT_DELIVERY_AGENT_ORDER_ITEM_STATUS_LIST.includes(record.orderItemStatus)) {
              return (
                <Select
                  list={deliveryAgentList}
                  onChange={(id) =>
                    setData({
                      path: ['orderManagementList', 'list', index, 'deliveryAgentId'],
                      value: id,
                    })
                  }
                  disabled={record.deliveryType !== ORDER_DELIVERY_TYPE.PARCEL_DELIVERY}
                  value={value}
                  size="default"
                  isTable={true}
                />
              );
            } else if (record.orderItemStatus >= ORDER_ITEM_STATUS.DELIVERY_COMPLETE && value) {
              /**
               *  배송완료(7), 구매확정(8), 취소(9), 반품(10), 교환(11) && 택배, 등기, 소포
               */
              return deliveryAgentList.find((item) => item.id === value).name;
            } else return '-';
          },
        },
        {
          /**
           *  송장번호
           *  - 상품 주문 상태가 상품 준비(4), 배송 지연(5), 배송 중(6)인 경우에만
           *  송장번호를 입력 또는 변경을 할 수 있음
           *  그 외의 경우에는 송장번호 값이 있으면 보여주고 없으면 '-'를 보여줌
           */
          title: <TableColumnTitle className="invoice-number" title="송장번호" />,
          dataIndex: 'invoiceNumber',
          render: (value, record, index) => {
            /**
             *  상품 준비(4), 배송 지연(5), 배송 중(6)
             */
            if (ENABLE_SELECT_DELIVERY_AGENT_ORDER_ITEM_STATUS_LIST.includes(record.orderItemStatus)) {
              return (
                <Input
                  value={value}
                  onKeyUp={(e) => numberKeyPress(e)}
                  onChange={(value) =>
                    setData({
                      path: ['orderManagementList', 'list', index, 'invoiceNumber'],
                      value: value.replace(/[^0-9]/g, ''),
                    })
                  }
                  disabled={record.deliveryType !== ORDER_DELIVERY_TYPE.PARCEL_DELIVERY}
                  maxLength={MAXIMUM_NUMBER_OF_INVOICE_NUMBER}
                />
              );
            } else return setDash(value);
          },
        },
        {
          title: '배송추적',
          dataIndex: 'deliveryTracking',
          render: (value, record) => (
            <OpenOrderDeliveryTracking
              deliveryId={record.deliveryId}
              targetStatus={record.orderItemStatus}
              deliveryType={record.deliveryType}
            />
          ),
        },
        {
          title: '주문 상태',
          dataIndex: 'orderItemStatus',
          render: (value) => ORDER_ITEM_STATUS_TEXT[value],
        },
      ],
    },
    {
      title: <TableColumnTitle title="주문건 처리 일시" color="primary" />,
      children: [
        {
          title: '주문 일시',
          dataIndex: 'orderDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '결제 일시',
          dataIndex: 'paymentDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '상품 준비 일시',
          dataIndex: 'deliveryPrepareDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '발송 처리 일시',
          dataIndex: 'deliveryDispatchDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
        {
          title: '배송 완료 일시',
          dataIndex: 'deliveryCompleteDate',
          render: (value) => setDateFormat(value, DATE_FORMAT.YMD_HM_DOT_FORMAT),
        },
      ],
    },
    {
      title: <TableColumnTitle title="구매자/수취인" color="primary" />,
      children: [
        {
          title: '구매 횟수',
          dataIndex: 'buyerPurchaseCnt',
          render: (value) => value,
        },
        {
          title: '구매자ID',
          dataIndex: 'buyerAccount',
          render: (value) => setBuyerAccount(value),
        },
        {
          title: '구매자명',
          dataIndex: 'buyerName',
        },
        {
          title: '수취인명',
          dataIndex: 'recipientName',
        },
      ],
    },
    {
      title: <TableColumnTitle title="구매상품 정보" color="primary" />,
      children: [
        {
          title: '브랜드명',
          dataIndex: 'brandName',
          render: (value, record) => <OpenBrandDetail text={value} storeId={record.storeId} />,
        },
        {
          title: '브랜드 ID',
          dataIndex: 'storeId',
          render: (value) => <OpenBrandDetail text={value} storeId={value} />,
        },
        {
          title: '상품번호',
          dataIndex: 'productId',
          render: (value) => <OpenProductDetail text={value} productId={value} />,
        },
        {
          title: '상품타입',
          dataIndex: 'productType',
          render: (value) => setDash(PRODUCT_MANAGEMENT_TYPE_TEXT[value]),
        },
        {
          title: '관리처',
          dataIndex: 'store',
          render: (value) => setDash(BRAND_TYPE_TEXT[value]),
        },
        {
          title: '제품번호',
          dataIndex: 'productNumber',
          render: (value) => setDash(value),
        },
        {
          title: <TableColumnTitle title="상품명" filterValue={filterOption.productName} />,
          dataIndex: 'productName',
          filterDropdown: ({ confirm }) => (
            <TableFilterInput
              placeholder="상품명 검색"
              option={filterOption.productName}
              confirm={confirm}
              onFilter={(productName) => onFilter({ productName })}
            />
          ),
          render: (value, record) => <OpenPreviewProductDetail text={value} productId={record.productId} />,
        },
        {
          title: <TableColumnTitle title="옵션정보" filterValue={filterOption.productOption} />,
          dataIndex: 'productOption',
          filterDropdown: ({ confirm }) => (
            <TableFilterInput
              placeholder="옵션정보 검색"
              option={filterOption.productOption}
              confirm={confirm}
              onFilter={(productOption) => onFilter({ productOption })}
            />
          ),
          render: (value) => setDash(value),
        },
        {
          title: '옵션 제품번호',
          dataIndex: 'productOptionNumber',
          render: (value) => setDash(value),
        },
        {
          title: '수량',
          dataIndex: 'quantity',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '상품 가격(정가)',
          dataIndex: 'productOriginalPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '상품 할인액(차감된 금액)',
          dataIndex: 'productDiscountPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '옵션가격',
          dataIndex: 'productOptionPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '쿠폰',
          dataIndex: 'usedCoupon',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: 'Apoint',
          dataIndex: 'usedPoint',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '상품 별 총 주문금액',
          dataIndex: 'totalPrice',
          render: (value) => setNumberWithComma(value),
        },
        {
          title: '추가 배송비',
          dataIndex: 'deliveryExtraFee',
          render: (value) => setNumberWithComma(value),
        },
      ],
    },
    {
      title: <TableColumnTitle title="배송 수취 정보" color="primary" />,
      children: [
        {
          title: '구매자 연락처',
          dataIndex: 'buyerPhone',
          render: (value) => setPhoneFormat(value),
        },
        {
          title: '수취인 연락처',
          dataIndex: 'recipientPhone',
          render: (value) => setPhoneFormat(value),
        },
        {
          title: '배송지',
          dataIndex: 'address',
          render: (value, record) =>
            record.shippingAddress1 &&
            record.shippingAddress2 &&
            `${record.shippingAddress1} ${record.shippingAddress2}`,
        },
        {
          title: <TableColumnTitle title="우편번호" />,
          dataIndex: 'shippingPostCode',
          render: (value) => setDash(value),
        },
        {
          title: '배송메시지',
          dataIndex: 'shippingMessage',
          render: (value) => setDash(value),
        },
      ],
    },
    {
      title: <TableColumnTitle title="결제 정보" color="primary" />,
      children: [
        {
          title: <TableColumnTitle title="결제 수단" />,
          dataIndex: 'payMethod',
          render: (value) => PAY_METHOD_TYPE_TEXT[value],
        },
      ],
    },
  ];
};

export default columns;
